// A24 «Tizim sozlamalari» — module-local data layer for the 15 config/infra
// screens (A24.02–A24.17). Does NOT touch `lib/adapters.ts` / `i18n/*` (HOT).
// Pattern mirrors `developers/devApi.ts`: fail-soft reads, typed writes.
//
// Most A24 screens are platform-wide app_settings key/value pairs, so they read
// and write the REAL `/admin/settings/system` endpoint (SettingsService). The
// backend `systemSettingsBody` struct accepts a known subset today; extra keys
// fail-soft (GET → default, PUT → 200 no-op) and persist once the struct is
// extended — exactly how `/admin/developers/*` shipped incrementally.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

// ───────────────────────── shared helpers (local copy) ─────────────────────

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

// ───────────────────────── system config (key/value) ───────────────────────

/** Normalised app_settings snapshot (every value stringified for form state). */
export type ConfigMap = Record<string, string>

/**
 * GET /admin/settings/system — real config store (A24.01). Returns the known
 * subset today; unknown keys are simply absent → callers fall back to defaults.
 * Fail-soft to `{}` so a form still renders (with its defaults) if the read errors.
 */
export async function fetchSystemConfig(signal?: AbortSignal): Promise<ConfigMap> {
  try {
    const d = await api.get<Record<string, unknown>>(
      '/admin/settings/system',
      signal ? { signal } : undefined,
    )
    const out: ConfigMap = {}
    for (const [k, v] of Object.entries(d ?? {})) out[k] = v == null ? '' : String(v)
    return out
  } catch {
    return {}
  }
}

/** JSON value written back per field — number/bool typed to match the backend. */
export type ConfigValue = string | number | boolean

/**
 * PUT /admin/settings/system — persists a partial key/value patch. Throws on a
 * real validation/permission error (surfaced as a toast); unknown greenfield
 * keys return 200 without persisting until the backend struct is extended.
 */
export function updateSystemConfig(patch: Record<string, ConfigValue>): Promise<unknown> {
  return api.put('/admin/settings/system', patch)
}

// ───────────────────────── field / section model ───────────────────────────

export type FieldType = 'text' | 'number' | 'bool' | 'color' | 'textarea' | 'password' | 'select'

/** One editable app_settings key. `def` seeds the form when the key is absent. */
export interface FieldSpec {
  key: string
  label: string
  hint?: string
  type?: FieldType // default 'text'
  def?: string
  min?: number
  max?: number
  placeholder?: string
  options?: { value: string; label: string }[]
  mono?: boolean
  /** Secret (write-only): never pre-filled, only sent when the admin types one. */
  secret?: boolean
}

export interface ConfigSection {
  title?: string
  fields: FieldSpec[]
}

/** Coerce a form string into the JSON type the backend expects for this field. */
export function coerceValue(spec: FieldSpec, raw: string): ConfigValue {
  if (spec.type === 'number') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }
  if (spec.type === 'bool') return raw === 'true' || raw === '1'
  return raw
}

// ───────────────────────── cache / redis (A24.10, REAL) ────────────────────

/** GET /admin/infra/redis → `redis` (A17.15). Matches services.RedisStats. */
export interface RedisStats {
  version: string
  uptime_seconds: number
  connected_clients: number
  used_memory: number
  maxmemory: number
  used_memory_pct: number
  maxmemory_policy: string
  frag_ratio: number
  db_keys: number
  keyspace_hits: number
  keyspace_misses: number
  hit_rate: number
  evicted_keys: number
  expired_keys: number
  ops_per_sec: number
  aof_enabled: boolean
  rdb_last_save: number
}

export async function fetchRedisStats(signal?: AbortSignal): Promise<RedisStats | null> {
  try {
    const d = await api.get<{ redis: RedisStats }>(
      '/admin/infra/redis',
      signal ? { signal } : undefined,
    )
    return d?.redis ?? null
  } catch {
    return null
  }
}

export type CacheScope = 'all' | 'config' | 'users'

/** POST /admin/devtools/cache/clear {scope} (A27.11) — real config-cache flush. */
export function clearCache(scope: CacheScope): Promise<{ ok: boolean; scope: string }> {
  return api.post<{ ok: boolean; scope: string }>('/admin/devtools/cache/clear', { scope })
}

// ───────────────────────── skeleton infra (no data source yet) ─────────────

/** A24.06 — a registered push credential (APNs/FCM/WebPush). Greenfield. */
export interface PushCertRow {
  id: number
  platform: string
  kind: string
  identifier: string
  expires_at: string | null
  status: string
}

/** A24.08 — a CDN edge/pull-zone. Greenfield. */
export interface CdnNodeRow {
  id: number
  region: string
  endpoint: string
  status: string
  hit_ratio: number
}

export function fetchPushCerts(params: FetchParams): Promise<FetchResult<PushCertRow>> {
  return safeList<PushCertRow>('/admin/settings/push-certs', 'certs', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.platform} ${r.kind} ${r.identifier}`),
  )
}

export function fetchCdnNodes(params: FetchParams): Promise<FetchResult<CdnNodeRow>> {
  return safeList<CdnNodeRow>('/admin/settings/cdn', 'nodes', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.region} ${r.endpoint}`),
  )
}

// ───────────────────────── module-local UZ strings ─────────────────────────
// (i18n HOT → keyга ko'chiriladi; hozircha modul ichida, devApi.DEV_STR kabi.)
export const SYS_STR = {
  save: 'Saqlash',
  saving: 'Saqlanmoqda…',
  saved: 'Sozlamalar saqlandi',
  saveError: 'Saqlashda xatolik',
  loading: 'Yuklanmoqda…',
  loadError: 'Sozlamalarni yuklab bo’lmadi',
  retry: 'Qayta urinish',
  refresh: 'Yangilash',
  greenfieldNote:
    'Bu sozlamalar platforma app_settings kalitlari sifatida saqlanadi. Backend kaliti ulanmaguncha qiymatlar faqat ko’rsatiladi.',
  secretHint: 'Maxfiy — o’zgartirish uchungina to’ldiring (bo’sh qolsa tegilmaydi)',
  on: 'Yoqilgan',
  off: 'O’chirilgan',

  // A24.02 Brending
  brandingTitle: 'Brending',
  brandingDesc: 'Logo, ranglar va ilova nomi',
  secBrandIdentity: 'Identifikatsiya',
  secBrandColors: 'Ranglar',
  fAppName: 'Ilova nomi',
  fLogoUrl: 'Logo URL',
  fLogoDarkUrl: 'Logo (qorong’u mavzu) URL',
  fFaviconUrl: 'Favicon URL',
  fPrimaryColor: 'Asosiy rang',
  fAccentColor: 'Urg’u rang',

  // A24.03 Domenlar
  domainsTitle: 'Domenlar konfiguratsiyasi',
  domainsDesc: 'API, web, WebSocket va CDN domenlari',
  fApiDomain: 'API domeni',
  fWebDomain: 'Web domeni',
  fWsDomain: 'WebSocket domeni',
  fCdnDomain: 'CDN domeni',
  fMediaDomain: 'Media domeni',

  // A24.04 SMTP
  smtpTitle: 'Email sozlamalari (SMTP)',
  smtpDesc: 'Tranzaksion pochta yuborish serveri',
  secSmtpServer: 'Server',
  secSmtpSender: 'Jo’natuvchi',
  fSmtpHost: 'SMTP host',
  fSmtpPort: 'Port',
  fSmtpUser: 'Foydalanuvchi',
  fSmtpPass: 'Parol',
  fSmtpTls: 'TLS/SSL',
  fSmtpFrom: 'From manzili',
  fSmtpFromName: 'From nomi',

  // A24.07 Fayl saqlash
  storageTitle: 'Fayl saqlash sozlamalari',
  storageDesc: 'Obyekt saqlash (S3/MinIO) parametrlari',
  fStorageProvider: 'Provayder',
  fStorageBucket: 'Bucket',
  fStorageRegion: 'Region',
  fStorageEndpoint: 'Endpoint',
  fStorageMaxMb: 'Fayl maksimal hajmi (MB)',

  // A24.09 Qidiruv indeksi
  searchTitle: 'Qidiruv indeksi',
  searchDesc: 'To’liq-matn qidiruv provayderi va indeks',
  fSearchEnabled: 'Qidiruvni yoqish',
  fSearchProvider: 'Provayder',
  fSearchIndex: 'Indeks nomi',
  fSearchHost: 'Host',
  fSearchMinChars: 'Minimal belgilar',

  // A24.10 Kesh boshqaruvi (REAL)
  cacheTitle: 'Kesh boshqaruvi',
  cacheDesc: 'Redis holati va konfiguratsiya keshini tozalash',
  cacheClear: 'Keshni tozalash',
  cacheClearing: 'Tozalanmoqda…',
  cacheCleared: 'Kesh tozalandi',
  cacheClearError: 'Keshni tozalab bo’lmadi',
  cacheScope: 'Doira',
  cacheScopeAll: 'Barchasi',
  cacheScopeConfig: 'Konfiguratsiya',
  cacheScopeUsers: 'Foydalanuvchilar',
  confirmClearTitle: 'Keshni tozalash',
  confirmClear: 'Konfiguratsiya keshi tozalanadi va keyingi o’qishda qayta yuklanadi. Davom etilsinmi?',
  cancel: 'Bekor qilish',
  redisDown: 'Redis mavjud emas',
  mVersion: 'Versiya',
  mUptime: 'Ishlash vaqti',
  mClients: 'Ulangan mijozlar',
  mMemory: 'Xotira',
  mHitRate: 'Hit-rate',
  mKeys: 'Kalitlar',
  mOps: 'Ops/sek',
  mPolicy: 'Xotira siyosati',
  mEvicted: 'Chiqarilgan kalitlar',

  // A24.11 Ro'yxatdan o'tish
  regTitle: 'Ro’yxatdan o’tish sozlamalari',
  regDesc: 'Yangi foydalanuvchi ro’yxatga olish qoidalari',
  fRegOpen: 'Ro’yxatdan o’tish ochiq',
  fRegInvite: 'Faqat taklif bilan',
  fRegMinAge: 'Minimal yosh',
  fRegVerifyPhone: 'Telefon tasdiqlash majburiy',
  fRegAllowedCountries: 'Ruxsat etilgan davlatlar (ISO, vergul bilan)',
  fRegBlockedCountries: 'Bloklangan davlatlar (ISO, vergul bilan)',
  fRegMaxPerIp: 'Bir IP uchun maksimal akkaunt',

  // A24.12 Media limitlari
  mediaTitle: 'Media limitlari',
  mediaDesc: 'Rasm/video/fayl/ovoz uchun maksimal hajm',
  fMaxPhotoMb: 'Rasm (MB)',
  fMaxVideoMb: 'Video (MB)',
  fMaxFileMb: 'Fayl (MB)',
  fMaxVoiceSec: 'Ovozli xabar (sekund)',
  fMaxGifMb: 'GIF (MB)',
  fMaxAlbumItems: 'Albomdagi elementlar',

  // A24.13 Xabar limitlari
  msgTitle: 'Xabar limitlari',
  msgDesc: 'Xabar uzunligi va yuborish tezligi',
  fMsgMaxLen: 'Maksimal uzunlik (belgi)',
  fMsgRatePerMin: 'Daqiqasiga xabar',
  fMsgEditWindow: 'Tahrirlash oynasi (daqiqa)',
  fMsgForwardMax: 'Bir martada forward',
  fMsgCaptionMax: 'Sarlavha maksimal (belgi)',

  // A24.14 Guruh/kanal limitlari
  groupTitle: 'Guruh/kanal limitlari',
  groupDesc: 'A’zolar, adminlar va qadalgan xabarlar chegarasi',
  fGroupMaxMembers: 'Guruh a’zolari (maks)',
  fChannelMaxSubs: 'Kanal obunachi (maks)',
  fGroupMaxAdmins: 'Adminlar (maks)',
  fGroupMaxPinned: 'Qadalgan xabar (maks)',
  fGroupsPerUser: 'Foydalanuvchi guruhi (maks)',

  // A24.15 Bot limitlari
  botTitle: 'Bot limitlari',
  botDesc: 'Botlar soni va so’rov tezligi',
  fBotMaxPerUser: 'Foydalanuvchi boti (maks)',
  fBotMsgRate: 'Sekundiga xabar',
  fBotWebhookMax: 'Webhook ulanishi (maks)',
  fBotInlineRate: 'Inline so’rov/sek',
  fBotFileMaxMb: 'Bot fayli (MB)',

  // A24.16 Premium paketlar
  premiumTitle: 'Premium paketlari',
  premiumDesc: 'Premium tarif darajalari va imkoniyatlari',
  fPremiumEnabled: 'Premiumni yoqish',
  fPremiumTrialDays: 'Sinov muddati (kun)',
  fPremiumLiteName: 'Lite nomi',
  fPremiumProName: 'Pro nomi',
  fPremiumMaxUploadMb: 'Premium yuklash (MB)',
  fPremiumFeatures: 'Imkoniyatlar (vergul bilan)',

  // A24.17 Narxlar
  pricingTitle: 'Narxlar',
  pricingDesc: 'Premium obuna va servis narxlari',
  secPricingPremium: 'Premium obuna',
  secPricingMisc: 'Boshqa',
  fCurrency: 'Valyuta',
  fPriceMonth: 'Oylik narx',
  fPriceYear: 'Yillik narx',
  fPriceStarsRate: 'Yulduz kursi (1 = ?)',
  fGiftCommission: 'Sovg’a komissiyasi (%)',
  fWithdrawMin: 'Minimal yechish',

  // A24.06 Push sertifikatlari (skeleton)
  pushTitle: 'Push sertifikatlari',
  pushDesc: 'APNs / FCM / WebPush hisob ma’lumotlari (infra)',
  cPlatform: 'Platforma',
  cKind: 'Turi',
  cIdentifier: 'Identifikator',
  cExpires: 'Amal qilish muddati',
  cStatus: 'Holat',

  // A24.08 CDN (skeleton — endi config)
  cdnTitle: 'CDN sozlamalari',
  cdnDesc: 'Edge tugunlar va pull-zonalar (infra)',
  cRegion: 'Region',
  cEndpoint: 'Endpoint',
  cHitRatio: 'Hit-ratio',

  // A24.06 push sertifikatlari (JONLI CRUD)
  cCertName: 'Sertifikat nomi',
  addPushCert: 'Sertifikat qo’shish',

  // A24.08 CDN config (app_settings cdn_*)
  cdnSecCore: 'CDN provayder',
  cdnEnabled: 'CDN yoqilgan',
  cdnEnabledHint: 'Media/statik uzatishni CDN orqali yo’naltirish',
  cdnProvider: 'Provayder',
  cdnBaseUrl: 'Bazaviy URL',
  cdnZone: 'Zona / pull-zone',
  cdnNotes: 'Izohlar',
} as const
